import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getEventsByRestaurant } from '../services/eventService';
import { getRestaurantById } from '../services/restaurantService';
import './ProgramPage.css';

/**
 * ProgramPage — the full experiences "Program" listing for one restaurant.
 *
 * Same structure as the standalone KIKI'S Program page: a hero, then experiences
 * grouped by month as a timeline (big date number, portrait cover image, title,
 * description, "Reserve Your Spot"). Each Reserve button opens the dedicated
 * /experience/:id booking page. Reached from the "<Restaurant> Program" button
 * on the experience page (and linkable directly).
 *
 * Route: /program/:restaurantId
 */

const fmtOneTime = (t) => {
  if (!t) return '';
  const [h, m] = t.trim().split(':');
  const hr = parseInt(h, 10);
  if (Number.isNaN(hr)) return t;
  const ampm = hr >= 12 ? 'PM' : 'AM';
  const h12 = hr % 12 || 12;
  return `${h12}:${m ?? '00'} ${ampm}`;
};

// "21:00 - 23:00" or "21:00" → "9:00 PM – 11:00 PM" / "9:00 PM"
const fmtTime = (t) => {
  if (!t) return '';
  const parts = t.split(/\s*(?:–|-|to)\s*/i).filter(Boolean);
  return parts.length === 2 ? parts.map(fmtOneTime).join(' – ') : fmtOneTime(t);
};

const getDateInfo = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return null;
  return {
    day: String(d.getDate()),
    month: d.toLocaleString('en', { month: 'short' }).toUpperCase(),
    dayOfWeek: d.toLocaleString('en', { weekday: 'long' }).toUpperCase(),
  };
};

// Group events by "Month Year", each group sorted soonest-first.
const groupByMonth = (events) => {
  const grouped = new Map();
  events.forEach((ev) => {
    const raw = ev.date || ev.eventDate;
    if (!raw) return;
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) return;
    const key = `${d.toLocaleString('en', { month: 'long' })} ${d.getFullYear()}`;
    if (!grouped.has(key)) grouped.set(key, []);
    grouped.get(key).push(ev);
  });
  grouped.forEach((list) =>
    list.sort((a, b) => new Date(a.date || a.eventDate) - new Date(b.date || b.eventDate)),
  );
  return grouped;
};

const ProgramPage = () => {
  const { restaurantId } = useParams();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [restaurant, setRestaurant] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!restaurantId) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      const [evRes, r] = await Promise.all([
        getEventsByRestaurant(restaurantId).catch(() => ({ events: [] })),
        getRestaurantById(restaurantId).catch(() => null),
      ]);
      if (cancelled) return;
      const list = (evRes.events || []).filter((e) => e.isActive !== false);
      setEvents(list);
      setRestaurant(r);
      setLoading(false);
    };
    load();
    return () => { cancelled = true; };
  }, [restaurantId]);

  const customColorStyles = restaurant
    ? {
        '--primary-purple': restaurant.widgetPrimaryColor || '#1A1A1A',
        '--secondary-purple': restaurant.widgetSecondaryColor || '#2D2D2D',
        '--accent-lime': restaurant.widgetAccentColor || '#BFFF00',
        '--text-dark': restaurant.widgetTextColor || '#1a1a1a',
      }
    : undefined;

  const restaurantName = restaurant?.name || '';
  const grouped = Array.from(groupByMonth(events));

  return (
    <div className="program-page" style={customColorStyles}>
      {/* Hero */}
      <header className="program-hero">
        {restaurantName && <p className="program-eyebrow">{restaurantName}</p>}
        <h1 className="program-title">The Program</h1>
        <p className="program-intro">
          Discover our upcoming experiences and reserve your spot.
        </p>
      </header>

      <section className="program-body">
        {loading ? (
          <div className="program-loading">
            <div className="widget-spinner"></div>
            <p>Loading the line-up…</p>
          </div>
        ) : events.length === 0 ? (
          <p className="program-empty">No experiences are on sale right now — check back soon.</p>
        ) : (
          grouped.map(([monthYear, monthEvents]) => (
            <div key={monthYear} className="program-month">
              <h2 className="program-month-title">{monthYear}</h2>

              <div className="program-events">
                {monthEvents.map((ev) => {
                  const dateInfo = getDateInfo(ev.date || ev.eventDate);
                  const img = ev.mobileCoverImageUrl || ev.imageUrl;
                  const category = ev.categories?.[0]?.title || '';
                  const desc = ev.description || ev.shortDescription || '';
                  return (
                    <article key={ev.id} className="program-event">
                      {/* Date column */}
                      <div className="program-event-date">
                        {dateInfo && (
                          <>
                            <span className="program-event-day">{dateInfo.day}</span>
                            <span className="program-event-month">{dateInfo.month}</span>
                            <span className="program-event-dow">{dateInfo.dayOfWeek}</span>
                          </>
                        )}
                      </div>

                      {/* Content column: portrait image + details */}
                      <div className="program-event-content">
                        {img && (
                          <div className="program-event-image">
                            <img
                              src={img}
                              alt={ev.title || ev.name}
                              onError={(e) => { e.currentTarget.parentElement.style.display = 'none'; }}
                            />
                            {ev.isInvitationOnly && (
                              <span className="program-event-private">
                                <i className="bi bi-lock-fill"></i> Private
                              </span>
                            )}
                          </div>
                        )}

                        <div className="program-event-details">
                          <div className="program-event-meta">
                            {category && <span className="program-event-category">{category.toUpperCase()}</span>}
                            {ev.time && (
                              <span className="program-event-time">
                                <i className="bi bi-clock me-1"></i>{fmtTime(ev.time)}
                              </span>
                            )}
                          </div>

                          <h3 className="program-event-title">{ev.title || ev.name}</h3>

                          {desc && <p className="program-event-desc">{desc}</p>}

                          <button
                            type="button"
                            className="program-event-cta"
                            onClick={() => navigate(`/experience/${ev.id}`)}
                          >
                            Reserve Your Spot <i className="bi bi-arrow-right ms-1"></i>
                          </button>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            </div>
          ))
        )}
      </section>
    </div>
  );
};

export default ProgramPage;
